import { Stack, Group, MultiSelect, Checkbox } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { Route, Search } from ".."
import { ActivityListItemCard } from "../../../-components/activity-list-item-card"
import { HideableFilterGroup } from "../../../-components/hideable-filter-group"
import { InfinitScrollingList } from "../../../-components/infinit-scrolling-list"
import { ListTableActionIconGroup } from "../../../-components/list-table-action-icon-group"
import { SeasonMultiSelect } from "../../../-components/season-multiselect"
import { useUserLocalStorage } from "../../../-components/use-user-local-storage"
import { UserLicenseRestrictedView } from "../../../-components/user-license-restricted-view"
import { Page } from "../../../../../-components/Page"
import { TreePillsInput } from "../../../../../-components/tree/tree-pills-input"
import { ActivityDto } from "../../../../../@client/Theasoft_ApiServer_Activities"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { useBreakpoints } from "../../../../../utils/use-breakpoints"
import { ExtendedActivityTable } from "./extended-activity-table"
import { MinimalActivityTable } from "./minimal-acitvity-table"
import { useGetMyLicenses } from "../../../-components/use-get-my-licenses"

export function ActivitiesPage() {
  const search = Route.useSearch()
  const { view, activityTypeNodes, productionIds, showCompleted, seasonIds } =
    search
  const [storedView, setStoredView] = useUserLocalStorage<"table" | "list">({
    key: "activities-view",
    defaultValue: "list",
  })
  const [showFilters, setShowFilters] = useUserLocalStorage<boolean>({
    key: "activities-show-filters",
    defaultValue: false,
  })
  const [storedShowCompleted, setStoredShowCompleted] =
    useUserLocalStorage<boolean>({
      key: "activities-show-completed",
      defaultValue: false,
    })
  const [storedActivityTypeNodes, setStoredActivityTypeNodes] =
    useUserLocalStorage<string[]>({
      key: "activities-activity-type-filter",
      defaultValue: [],
    })
  React.useEffect(() => {
    updateSearch({
      view: view || storedView || "list",
      activityTypeNodes: activityTypeNodes || storedActivityTypeNodes || [],
      productionIds: productionIds || [],
      showCompleted: showCompleted || storedShowCompleted || false,
      seasonIds: seasonIds || storedSeasonIds || [],
    })
  }, [])
  const { data: userLicenses } = useGetMyLicenses()

  React.useEffect(() => {
    if (
      !userLicenses.some((v) => v.product === "theaDispoWebManager") &&
      view === "table"
    ) {
      setStoredView("list")
      updateSearch({ view: "list" })
    }
  }, [userLicenses, view])

  const { data: seasonFilterDto } = useSuspenseQueryGet(
    "/api/Production/GetDefaultSeason",
    null
  )
  const [storedSeasonIds, setStoredSeasonIds] = useUserLocalStorage<number[]>({
    key: "activities-season-filter",
    defaultValue: seasonFilterDto.defaultValue,
  })
  const { data: productions } = useSuspenseQueryGet(
    "/api/Production/GetProductionsInSeasons",
    {
      seasonIds: seasonIds,
    }
  )
  const { xs } = useBreakpoints()
  const { t, i18n } = useTranslation("app_server")
  const { data: activityTypeTree } = useSuspenseQueryGet(
    "/api/ActivityType/GetActivityTypeTree",
    {
      language: i18n.language,
    }
  )

  const navigate = Route.useNavigate()

  const navigatToActivityId = (acitivity: ActivityDto) =>
    navigate({
      to: "/app/art/activities/$id",
      params: { id: acitivity.activityId.toString() },
      search: { tab: "start" },
    })
  function updateSearch(input: Partial<Search>) {
    navigate({
      search: {
        ...search,
        ...input,
      },
      replace: true,
    })
  }

  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <HideableFilterGroup.Button
              activeFilters={
                (activityTypeNodes?.length ? 1 : 0) +
                (seasonIds?.length ? 1 : 0) +
                (showCompleted ? 0 : 1)
              }
              visible={showFilters}
              onChange={(showFilters) => setShowFilters(!showFilters)}
            />
            <UserLicenseRestrictedView licenses={["theaDispoWebManager"]}>
              <ListTableActionIconGroup
                value={view}
                onChange={(value) => {
                  setStoredView(value)
                  updateSearch({ view: value })
                }}
              />
            </UserLicenseRestrictedView>
          </>
        ),
      }}
    >
      <Stack h={"100%"}>
        <Group>
          <MultiSelect
            w={xs ? "100%" : 400}
            key={"production-filter"}
            pointer
            clearable
            placeholder={
              productionIds && productionIds.length > 0
                ? ""
                : t("FilterProductions")
            }
            label={t("Productions")}
            searchable
            data={productions.map((v) => ({
              value: v.productionId.toString(),
              label: v.title,
            }))}
            value={productionIds?.map((v) => v.toString())}
            onChange={(value) =>
              updateSearch({ productionIds: value?.map((v) => parseInt(v)) })
            }
          />
          <HideableFilterGroup visible={showFilters}>
            {activityTypeNodes ? (
              <TreePillsInput
                w={xs ? "100%" : 400}
                label={t("ActivityTypes")}
                placeholder={t("FilterActivityTypes")}
                tree={activityTypeTree.tree}
                initialCheckedState={activityTypeNodes}
                initialExpandedState={activityTypeTree.initialExpandedState}
                onCheckedStateChange={(v) => {
                  setStoredActivityTypeNodes(v)
                  updateSearch({ activityTypeNodes: v })
                }}
              />
            ) : null}
            <SeasonMultiSelect
              w={xs ? "100%" : 400}
              data={seasonFilterDto.data}
              value={seasonIds}
              key={"season-filter"}
              onChange={(value) => {
                setStoredSeasonIds(value)
                updateSearch({ seasonIds: value })
              }}
            />
            <Checkbox
              key={"switch"}
              checked={!showCompleted}
              onChange={() => {
                setStoredShowCompleted(!showCompleted)
                updateSearch({ showCompleted: !showCompleted })
              }}
              style={{
                alignContent: "center",
                justifySelf: xs ? undefined : "center",
              }}
              labelPosition="left"
              label={t("FromToday")}
            />
          </HideableFilterGroup>
        </Group>
        {view === "table" ? (
          <UserLicenseRestrictedView licenses={["theaDispoWebManager"]}>
            <ExtendedActivityTable
              partialInput={{
                ...search,
              }}
              searchProps={{
                value: search,
                onChange: (value) => updateSearch(value),
              }}
              onClickRow={navigatToActivityId}
            />
          </UserLicenseRestrictedView>
        ) : xs ? (
          <InfinitScrollingList
            noFilter
            partialInput={{
              ...search,
            }}
            renderItem={(item) => (
              <ActivityListItemCard
                item={item}
                onClickItem={navigatToActivityId}
              />
            )}
            url="/api/Activity/GetActivityList"
          />
        ) : (
          <MinimalActivityTable
            partialInput={{
              ...search,
            }}
            onClickRow={navigatToActivityId}
          />
        )}
      </Stack>
    </Page>
  )
}
